import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import { getDepartmentOptions, getDivisionOptions, getEmploymentTypeOptions } from '../../api/options';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const WaveSelectField = require('../../../common/components/form/WaveSelectField').default as React.ComponentType<any>;

type OptionMap = Record<string, { id?: string | number; label?: string; name?: string }>;

const currentYear = new Date().getFullYear();

const yearOptions: OptionMap = Array.from({ length: 5 }, (_, i) => currentYear - i).reduce(
  (acc, y) => ({ ...acc, [y]: { label: y.toString() } }),
  {}
);

export interface OverviewFilters {
  division_id: string;
  department_ids: string[];
  employment_type_id: string;
  year: string;
}

export const INITIAL_FILTERS: OverviewFilters = {
  division_id: 'all',
  department_ids: ['all'],
  employment_type_id: 'all',
  year: currentYear.toString()
};

export const toPassportTableFilters = (filters: OverviewFilters): Record<string, unknown> => {
  const q: Record<string, unknown> = {};

  if (filters.division_id && filters.division_id !== 'all') {
    q.effective_division_id_eq = filters.division_id;
  }

  const departmentIds = filters.department_ids.filter((id) => id && id !== 'all');
  if (departmentIds.length > 0) {
    q.department_id_in = departmentIds;
  }

  if (filters.employment_type_id && filters.employment_type_id !== 'all') {
    q.employment_type_id_eq = filters.employment_type_id;
  }

  // The passport report derives its period from a date (institute.passport_period),
  // not a year, so send a date that falls inside the selected year's period. The
  // widgets treat year Y as 1 Jul (Y-1) - 30 Jun Y, and 30 Jun Y sits in that
  // period for every institute anniversary month.
  if (filters.year) {
    q.date_eq = `${filters.year}-06-30`;
  }

  return q;
};

interface OverviewFilterProps {
  filters: OverviewFilters;
  onChange: (filters: OverviewFilters) => void;
  onFilter: (filters: OverviewFilters) => void;
}

const OverviewFilter = ({ filters, onChange, onFilter }: OverviewFilterProps) => {
  const [divisionOptions, setDivisionOptions] = useState<OptionMap>({});
  const [departmentOptions, setDepartmentOptions] = useState<OptionMap>({});
  const [filteredDepartments, setFilteredDepartments] = useState<OptionMap>({});
  const [employmentTypeOptions, setEmploymentTypeOptions] = useState<OptionMap>({});

  useEffect(() => {
    Promise.all([getDivisionOptions(), getDepartmentOptions(), getEmploymentTypeOptions()]).then(
      ([divisions, departments, employmentTypes]) => {
        setDivisionOptions(divisions ?? {});
        setDepartmentOptions(departments ?? {});
        setEmploymentTypeOptions(employmentTypes ?? {});
      }
    );
  }, []);

  useEffect(() => {
    const divisionIdInt = parseInt(filters.division_id);
    if (divisionIdInt) {
      const filtered = Object.fromEntries(
        Object.entries(departmentOptions).filter(
          ([, dept]) => (dept as { division_id?: number }).division_id === divisionIdInt
        )
      );
      setFilteredDepartments(filtered);
    } else {
      setFilteredDepartments(departmentOptions);
    }
  }, [filters.division_id, departmentOptions]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    onChange({
      ...filters,
      [name]: value,
      ...(name === 'division_id' ? { department_ids: ['all'] } : {})
    });
  };

  const handleDepartmentChange = (event: { target: { value: string[] } }) => {
    const { value } = event.target;
    if (!Array.isArray(value)) return;
    const prevHadAll = filters.department_ids.indexOf('all') > -1;
    const nextHasAll = value.indexOf('all') > -1;

    const department_ids =
      nextHasAll && !prevHadAll
        ? ['all']
        : value.filter((v) => v !== 'all').length > 0
        ? value.filter((v) => v !== 'all')
        : ['all'];
    onChange({ ...filters, department_ids });
  };

  const handleClear = () => {
    onChange(INITIAL_FILTERS);
    onFilter(INITIAL_FILTERS);
  };

  const handleFilter = () => {
    onFilter(filters);
  };

  return (
    <Paper>
      <Grid container alignItems="center" justifyContent="space-between" sx={{ px: '25px', pt: '20px', pb: '10px' }}>
        <Typography variant="h6">Filter</Typography>
        <Grid>
          <Typography
            component="a"
            onClick={handleClear}
            sx={{
              color: 'text.primary',
              fontSize: 13,
              opacity: 0.7,
              mr: '20px',
              cursor: 'pointer',
              '&:hover': { opacity: 1 }
            }}
          >
            Clear
          </Typography>
          <Button color="secondary" onClick={handleFilter} size="small" variant="contained">
            <Typography variant="button">Filter</Typography>
          </Button>
        </Grid>
      </Grid>
      <Grid container sx={{ pb: '30px', px: '25px' }} columnSpacing="40px" rowSpacing="25px">
        <Grid size={3}>
          <WaveSelectField
            filterByAllItem
            filterByAllItemLabel="All"
            noValidation
            label="Division"
            menuItems={divisionOptions}
            name="division_id"
            onChange={handleChange}
            value={filters.division_id}
          />
        </Grid>
        <Grid size={3}>
          <WaveSelectField
            multiple
            filterByAllItem
            filterByAllItemLabel="All"
            noValidation
            label="Department"
            menuItems={filteredDepartments}
            name="department_ids"
            onChange={handleDepartmentChange}
            value={filters.department_ids}
          />
        </Grid>
        <Grid size={3}>
          <WaveSelectField
            filterByAllItem
            filterByAllItemLabel="All"
            noValidation
            label="Employment Type"
            menuItems={employmentTypeOptions}
            name="employment_type_id"
            onChange={handleChange}
            value={filters.employment_type_id}
          />
        </Grid>
        <Grid size={3}>
          <WaveSelectField
            noValidation
            label="Year"
            menuItems={yearOptions}
            name="year"
            onChange={handleChange}
            value={filters.year}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default OverviewFilter;
